import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import {
  getSession,
  loginWithPhone,
  logout as clearSession,
  writeSession,
  type LoginResult,
} from "@/lib/employeeAuth";
import type { Employee } from "@/lib/types";

type SessionState =
  | { status: "loading" }
  | { status: "unauthenticated" }
  | { status: "authenticated"; employee: Employee };

type SessionContextValue = {
  state: SessionState;
  employee: Employee | null;
  login: (rawPhone: string) => Promise<LoginResult>;
  logout: () => void;
  setEmployee: (employee: Employee) => void;
};

const EmployeeSessionContext = createContext<SessionContextValue | null>(null);

export function EmployeeSessionProvider({ children }: { children: ReactNode }) {
  // Synchronous init from sessionStorage — no flash of unauthenticated UI on refresh.
  const [employee, setEmployeeState] = useState<Employee | null>(() => getSession());

  const login = useCallback(async (rawPhone: string): Promise<LoginResult> => {
    const result = await loginWithPhone(rawPhone);
    if (result.ok) setEmployeeState(result.employee);
    return result;
  }, []);

  const logout = useCallback(() => {
    clearSession();
    setEmployeeState(null);
  }, []);

  const setEmployee = useCallback((next: Employee) => {
    writeSession(next);
    setEmployeeState(next);
  }, []);

  const value = useMemo<SessionContextValue>(() => {
    const state: SessionState = employee
      ? { status: "authenticated", employee }
      : { status: "unauthenticated" };
    return { state, employee, login, logout, setEmployee };
  }, [employee, login, logout, setEmployee]);

  return (
    <EmployeeSessionContext.Provider value={value}>
      {children}
    </EmployeeSessionContext.Provider>
  );
}

export function useEmployeeSession(): SessionContextValue {
  const ctx = useContext(EmployeeSessionContext);
  if (!ctx) {
    throw new Error("useEmployeeSession must be used within EmployeeSessionProvider");
  }
  return ctx;
}
